import { AddCircle, CheckCircle, RemoveCircle } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    IconButton,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";
import { MouseEvent, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { DynamicMarkdownContent } from "../../../components/dynamic-markdown/dynamic-markdown-content";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";

export interface IFAQProps {
    question?: string;
    answer?: string;
    slug?: string;
    isLoading?: boolean;
}

export const CollapsibleFAQ = ({
    className,
    question,
    answer,
    slug,
    isLoading,
}: IWithClassName & IFAQProps) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [copySuccess, setCopySuccess] = useState<boolean>(false);
    const accordionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const currentSlug = decodeURIComponent(
            window.location.hash.substring(1),
        );
        if (currentSlug === slug) {
            setIsExpanded(true);
            accordionRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [slug]);
    const toggleExpand = () => setIsExpanded(!isExpanded);

    const handleCopyClick = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        navigator.clipboard
            .writeText(window.location.href.split("#")[0] + "#" + (slug ?? ""))
            .then(() => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            })
            .catch((err) => console.error("Error copying text: ", err));
    };

    return (
        <Accordion
            className={className}
            expanded={isExpanded}
            onChange={toggleExpand}
            ref={accordionRef}
        >
            <AccordionSummary
                expandIcon={isExpanded ? <RemoveCircle /> : <AddCircle />}
                aria-controls="panel-content"
                id="panel-header"
            >
                {isLoading ? <Skeleton /> : <Typography>{question}</Typography>}
                <Tooltip title={copySuccess ? "Copied!" : "Copy Link"}>
                    <StyledIconButton
                        onClick={handleCopyClick}
                        disableFocusRipple={true}
                        disableRipple={true}
                        disableTouchRipple={true}
                    >
                        {copySuccess ? (
                            <CheckCircleStyle />
                        ) : (
                            <img src="/assets/link.svg" alt="Copy Link" />
                        )}
                    </StyledIconButton>
                </Tooltip>
            </AccordionSummary>
            <AccordionDetails>
                {isLoading ? (
                    <Skeleton />
                ) : (
                    <DynamicMarkdownContent markdown={answer} />
                )}
            </AccordionDetails>
        </Accordion>
    );
};

const CheckCircleStyle = styled(CheckCircle)(({ theme }: IWithThemeProps) => ({
    width: theme?.spacing(2.25),
    height: theme?.spacing(1.5),
}));

const StyledIconButton = styled(IconButton)(({ theme }: IWithThemeProps) => ({
    opacity: 0,
    transform: "translateX(-50%)",
    transition: "opacity 0.3s ease, transform 0.3s ease",
    marginLeft: theme?.spacing(1),

    ".MuiAccordionSummary-root:hover &": {
        opacity: 1,
        transform: "translateX(0)",
    },
}));

export default styled(CollapsibleFAQ)(({ theme }: IWithThemeProps) => ({
    "& .MuiAccordionSummary-root": {
        minHeight: theme?.spacing(8.75),
        "&.Mui-expanded": {
            minHeight: theme?.spacing(8.75),
        },
        span: {
            width: "100%",
        },
    },
    "& .faq-answer": {
        fontWeight: 300,
    },
}));
